// Pure, Firebase-free, React-free: turns a search string plus the app's loaded
// records into grouped results the header dropdown can render directly.
// Follows the selector pattern in unassignedLeads.js / duplicateLeads.js.
//
// Address field names differ per collection by history — `propertyaddress` on
// deals, `propertyAddress` on leads and open houses. Normalizing here means no
// caller ever has to care.

import { isOnLead } from "./leadOwnership";
import { visibleOpenHousesFor } from "./openHouseHelpers";

// Roles that see every agent's leads, mirroring main-listing-leads.jsx.
const PRIVILEGED_ROLES = ["Admin", "Supervisor", "Accountant"];
export const canSeeAllLeads = (user) =>
  PRIVILEGED_ROLES.includes(user?.role);

// THE rule for "may this user see this lead". Exported deliberately: every
// surface that lists leads must use this one predicate rather than reimplement
// it. Recently Deleted shipped its first draft with no rule at all and showed
// every agent's deleted leads — with a working permanent-delete button — to
// everyone (caught in review, 2026-08-11). Two copies of a privacy rule is one
// copy too many.
export const canSeeLead = (lead, user) =>
  canSeeAllLeads(user) || isOnLead(lead, user?.id, { ownerlessFallback: true });

// THE rule for "may this user see this deal". Mirrors the non-privileged branch
// of visibleDeals in main-deal-board.jsx, so an agent's trash holds the same
// deals their board does.
//
// Deliberately does NOT copy that function's TEAM_WIDE_STAGES exemption. Those
// stages are team-wide so live work is visible to anyone who might act on it;
// that is not a reason to show the whole team each other's deleted files.
export const canSeeDeal = (deal, user) =>
  PRIVILEGED_ROLES.includes(user?.role) ||
  (!!user?.id && (deal?.createdby === user.id || deal?.agentData === user.id));

const str = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));
const lower = (v) => str(v).toLowerCase();

const isDeleted = (r) => Number(r?.deletedAt) > 0;

// Search mirrors the Deals Board: everyone can find LIVE team-wide-stage deals
// (active, coming soon) because those already show on everyone's board, but a
// deal you don't own stays hidden once it is deleted or in a private stage.
// Deleted files are never team-wide.
const SEARCH_TEAM_WIDE_STAGES = new Set(["active", "coming soon"]);
const canFindDeal = (deal, user) =>
  canSeeDeal(deal, user) ||
  (!isDeleted(deal) && SEARCH_TEAM_WIDE_STAGES.has(lower(deal?.dealStage)));

// --- per-type normalizers: every record becomes the same shape ---

const fromDeal = (d) => ({
  type: "deal",
  id: str(d.createdat || d.id),
  title: str(d.propertyaddress) || "Untitled deal",
  subtitle: [str(d.firstName), str(d.lastName)].filter(Boolean).join(" ").trim()
    || str(d.clientLastName)
    || str(d.dealtype),
  badge: str(d.dealStage),
  haystack: [d.propertyaddress, d.firstName, d.lastName, d.clientLastName],
  route: `/dashboard/deals-detail/${str(d.createdat || d.id)}`,
  deleted: isDeleted(d),
  deletedAt: Number(d.deletedAt) || null,
});

// "unassigned" is a claim about ownership, so read it off assignedAgent — the
// field that actually decides it. assignedAgentName is a display cache and is
// routinely blank on assigned leads, which would libel them as unowned.
const agentLabel = (l) =>
  str(l.assignedAgentName) || (l.assignedAgent ? "assigned" : "unassigned");

const fromListingLead = (l) => ({
  type: "listingLead",
  id: str(l.id),
  title: str(l.propertyAddress) || str(l.contactName) || "Untitled lead",
  subtitle: [str(l.contactName), agentLabel(l)].filter(Boolean).join(" · "),
  badge: str(l.stage),
  haystack: [l.propertyAddress, l.contactName],
  route: `/dashboard/listings?lead=${str(l.id)}`,
  deleted: isDeleted(l),
  deletedAt: Number(l.deletedAt) || null,
});

const fromBuyerLead = (l) => ({
  type: "buyerLead",
  id: str(l.id),
  title: str(l.contactName) || str(l.propertyAddress) || "Untitled lead",
  subtitle: agentLabel(l),
  badge: str(l.stage),
  haystack: [l.contactName, l.propertyAddress],
  route: `/dashboard/buyers?lead=${str(l.id)}`,
  deleted: isDeleted(l),
  deletedAt: Number(l.deletedAt) || null,
});

const fromOpenHouse = (oh) => ({
  type: "openHouse",
  id: str(oh.id),
  title: str(oh.propertyAddress) || "Untitled open house",
  subtitle: str(oh.hostingAgentName) || str(oh.listingAgentName),
  badge: str(oh.date),
  haystack: [oh.propertyAddress, oh.hostingAgentName],
  route: `/dashboard/open-houses`,
  deleted: isDeleted(oh),
  deletedAt: Number(oh.deletedAt) || null,
});

const GROUPS = [
  { key: "deal", label: "Deals" },
  { key: "listingLead", label: "Listing leads" },
  { key: "buyerLead", label: "Buyer leads" },
  { key: "openHouse", label: "Open houses" },
];

const matches = (row, q) =>
  (row.haystack || []).some((f) => lower(f).includes(q));

const PER_GROUP = 5;
const OVERALL = 15;
// Slots the deleted group keeps even when live results would fill the list.
const DELETED_RESERVED = 3;

// Prefix matches first, then alphabetical. Deliberately simple: this is a
// jump-to-record box, not a relevance engine.
const rank = (q) => (a, b) => {
  const pa = lower(a.title).startsWith(q) ? 0 : 1;
  const pb = lower(b.title).startsWith(q) ? 0 : 1;
  if (pa !== pb) return pa - pb;
  return lower(a.title).localeCompare(lower(b.title));
};

// Where a deleted record now lives. Leads moved into their own pipeline's
// Deleted tab; deals stayed on Recently Deleted.
const DELETED_ROUTE = {
  deal: "/dashboard/recently-deleted",
  listingLead: "/dashboard/listings?view=deleted",
  buyerLead: "/dashboard/buyers?view=deleted",
};

export function selectSearchResults({
  query = "",
  deals = [],
  listingLeads = [],
  buyerLeads = [],
  openHouses = [],
  user = {},
} = {}) {
  const q = lower(query).trim();
  if (!q) return [];

  const mine = (l) => canSeeLead(l, user);

  const rows = [
    ...(deals || []).filter(Boolean).filter((d) => canFindDeal(d, user)).map(fromDeal),
    ...(listingLeads || []).filter(Boolean).filter(mine).map(fromListingLead),
    ...(buyerLeads || []).filter(Boolean).filter(mine).map(fromBuyerLead),
    // Open houses have their own long-standing visibility rule (hosting agent,
    // listing agent, or Admin/Supervisor). Search must not be a way around it.
    ...visibleOpenHousesFor((openHouses || []).filter(Boolean), user).map(fromOpenHouse),
  ].filter((r) => matches(r, q));

  const live = rows.filter((r) => !r.deleted).sort(rank(q));
  const gone = rows
    .filter((r) => r.deleted)
    .map((r) => ({ ...r, route: DELETED_ROUTE[r.type] || "/dashboard/recently-deleted" }))
    .sort((a, b) => (b.deletedAt || 0) - (a.deletedAt || 0));

  const groups = GROUPS.map(({ key, label }) => {
    const all = live.filter((r) => r.type === key);
    return { key, label, total: all.length, results: all.slice(0, PER_GROUP) };
  }).filter((g) => g.results.length);

  if (gone.length) {
    groups.push({
      key: "deleted", label: "Deleted",
      total: gone.length, results: gone.slice(0, PER_GROUP),
    });
  }

  // Overall cap trims from the end, so live groups (ordered first) win. But
  // trimming naively wipes the deleted group out completely: four full live
  // groups is already 20 rows against a budget of 15, so any broad query would
  // hide every deleted record — the exact thing this feature exists to surface.
  // Reserve the deleted group's slots up front and spend the rest on live rows.
  const deletedGroup = groups.find((g) => g.key === "deleted");
  const reserved = deletedGroup
    ? Math.min(deletedGroup.results.length, DELETED_RESERVED)
    : 0;

  let budget = OVERALL - reserved;
  return groups
    .map((g) => {
      if (g.key === "deleted") return { ...g, results: g.results.slice(0, reserved) };
      const results = g.results.slice(0, Math.max(0, budget));
      budget -= results.length;
      return { ...g, results };
    })
    .filter((g) => g.results.length);
}
